/**
 * Tutoriel interactif PathBlock
 */

import React, { useState } from 'react';

interface Step {
  title: string;
  icon: string;
  description: string;
  visual: React.ReactNode;
}

const STEPS: Step[] = [
  {
    title: 'Choisissez votre position',
    icon: '📍',
    description:
      'Avant la partie, cliquez une case sur votre rangée du bas pour choisir votre point de départ. Votre case d\'arrivée sera automatiquement placée à la même colonne, de l\'autre côté du plateau.',
    visual: (
      <div className="flex flex-col items-center gap-1 w-full">
        {/* Rangée cible (haut) */}
        <div className="flex items-center gap-2 w-full justify-center">
          <span className="text-teal-400 text-xs w-20 text-right">← Arrivée</span>
          <div className="flex gap-1">
            {[0,1,2,3,4].map((c) => (
              <div key={c} className={`w-8 h-8 rounded text-sm flex items-center justify-center border
                ${c === 2 ? 'bg-teal-700 border-teal-400 text-teal-200 font-bold' : 'bg-gray-700 border-gray-600'}`}>
                {c === 2 ? '★' : ''}
              </div>
            ))}
          </div>
          <span className="w-20" />
        </div>

        <div className="text-gray-600 text-xs py-1">· · · plateau · · ·</div>

        {/* Rangée de départ (bas) */}
        <div className="flex items-center gap-2 w-full justify-center">
          <span className="text-blue-400 text-xs w-20 text-right">← Départ</span>
          <div className="flex gap-1">
            {[0,1,2,3,4].map((c) => (
              <div key={c} className={`w-8 h-8 rounded text-sm flex items-center justify-center border transition-colors
                ${c === 2 ? 'bg-blue-700 border-blue-400 text-white font-bold' : 'bg-gray-500 border-gray-400 cursor-pointer text-gray-300'}`}>
                {c === 2 ? '●' : '·'}
              </div>
            ))}
          </div>
          <span className="w-20" />
        </div>
        <p className="text-gray-500 text-xs mt-2">La colonne choisie détermine votre case d'arrivée ↑</p>
      </div>
    ),
  },
  {
    title: 'Déplacez votre pion',
    icon: '🏃',
    description:
      'À chaque tour, déplacez votre pion d\'une case : haut, bas, gauche ou droite. Si l\'adversaire se trouve juste devant vous, vous pouvez sauter par-dessus.',
    visual: (
      <div className="flex flex-col items-center gap-2">
        <div className="grid grid-cols-3 gap-1">
          {[
            { bg: 'bg-gray-700', label: '' },
            { bg: 'bg-green-600', label: '↑' },
            { bg: 'bg-gray-700', label: '' },
            { bg: 'bg-green-600', label: '←' },
            { bg: 'bg-blue-600', label: '●', isCurrent: true },
            { bg: 'bg-green-600', label: '→' },
            { bg: 'bg-gray-700', label: '' },
            { bg: 'bg-green-600', label: '↓' },
            { bg: 'bg-gray-700', label: '' },
          ].map((cell, i) => (
            <div
              key={i}
              className={`w-10 h-10 rounded flex items-center justify-center text-sm font-bold border border-gray-600 ${cell.bg} text-white`}
            >
              {cell.label}
            </div>
          ))}
        </div>
        <p className="text-gray-500 text-xs mt-1">Les cases vertes indiquent les déplacements possibles</p>
      </div>
    ),
  },
  {
    title: 'Posez des murs',
    icon: '🧱',
    description:
      'Vous disposez de 10 murs que vous pouvez poser pour ralentir l\'adversaire. Chaque mur s\'étend sur 2 cases. Attention : il est interdit de bloquer totalement le chemin d\'un joueur vers sa case d\'arrivée.',
    visual: (
      <div className="flex flex-col items-center gap-2">
        <div className="grid grid-cols-4 gap-1">
          {Array.from({ length: 16 }).map((_, i) => {
            const row = Math.floor(i / 4);
            const col = i % 4;
            // Mur vertical entre col 1 et col 2, sur les lignes 0 et 1
            const hasWallRight = col === 1 && (row === 0 || row === 1);
            return (
              <div key={i} className="relative w-9 h-9">
                <div className="w-full h-full bg-gray-700 border border-gray-600 rounded" />
                {hasWallRight && (
                  <div className="absolute right-[-5px] top-0 w-[6px] h-full bg-orange-500 z-10" />
                )}
                {row === 1 && col === 0 && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-6 h-6 rounded-full bg-red-500 border border-white text-white text-xs flex items-center justify-center font-bold">1</div>
                  </div>
                )}
                {row === 1 && col === 3 && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-6 h-6 rounded-full bg-blue-500 border border-white text-white text-xs flex items-center justify-center font-bold">2</div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <p className="text-gray-500 text-xs">Le mur (orange) bloque le passage entre les deux joueurs</p>
      </div>
    ),
  },
  {
    title: 'Remportez la victoire',
    icon: '🏆',
    description:
      'Le premier joueur à atteindre sa case d\'arrivée (surlignée en vert-cyan) remporte la partie. Bloquez le chemin adverse tout en gardant le vôtre ouvert !',
    visual: (
      <div className="flex flex-col items-center gap-1">
        {/* Rangée du haut avec case cible */}
        <div className="flex gap-1">
          {[0,1,2,3,4].map((c) => (
            <div key={c} className={`w-8 h-8 rounded text-sm flex items-center justify-center border font-bold
              ${c === 2
                ? 'bg-teal-600 border-teal-400 ring-2 ring-teal-300 text-white'
                : 'bg-gray-700 border-gray-600'}`}>
              {c === 2 ? '★' : ''}
            </div>
          ))}
        </div>
        <div className="text-teal-500 text-xs my-1">↑ Votre case d'arrivée</div>
        {/* Pion proche de l'arrivée */}
        <div className="flex gap-1">
          {[0,1,2,3,4].map((c) => (
            <div key={c} className={`w-8 h-8 rounded flex items-center justify-center border
              ${c === 2 ? 'bg-green-700 border-green-500' : 'bg-gray-700 border-gray-600'}`}>
              {c === 2 && <div className="w-5 h-5 rounded-full bg-blue-400 border-2 border-white shadow" />}
            </div>
          ))}
        </div>
        <p className="text-gray-500 text-xs mt-2 text-center">Plus qu'un pas — le pion est sur la case verte !</p>
      </div>
    ),
  },
];

interface TutorialProps {
  onClose: () => void;
}

export const Tutorial: React.FC<TutorialProps> = ({ onClose }) => {
  const [step, setStep] = useState(0);
  const current = STEPS[step];

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-gray-800 border border-gray-700 rounded-2xl shadow-2xl w-full max-w-sm">
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-gray-700">
          <h2 className="text-white font-bold text-lg">Comment jouer</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-xl leading-none">✕</button>
        </div>

        {/* Barre de progression */}
        <div className="flex gap-2 px-5 pt-4">
          {STEPS.map((_, i) => (
            <button
              key={i}
              onClick={() => setStep(i)}
              className={`flex-1 h-1 rounded-full transition-colors ${
                i === step ? 'bg-blue-500' : i < step ? 'bg-blue-800' : 'bg-gray-600'
              }`}
            />
          ))}
        </div>

        {/* Contenu */}
        <div className="px-5 py-5 space-y-4">
          <div className="flex items-center gap-3">
            <span className="text-3xl">{current.icon}</span>
            <div>
              <p className="text-gray-500 text-xs">{step + 1} / {STEPS.length}</p>
              <h3 className="text-white font-semibold text-base leading-tight">{current.title}</h3>
            </div>
          </div>

          <p className="text-gray-300 text-sm leading-relaxed">{current.description}</p>

          <div className="bg-gray-900/60 rounded-xl p-4 flex items-center justify-center min-h-[140px]">
            {current.visual}
          </div>
        </div>

        {/* Navigation */}
        <div className="flex gap-3 px-5 pb-5">
          {step > 0 ? (
            <button onClick={() => setStep(step - 1)} className="btn btn-secondary flex-1">
              ← Précédent
            </button>
          ) : (
            <div className="flex-1" />
          )}

          {step < STEPS.length - 1 ? (
            <button onClick={() => setStep(step + 1)} className="btn btn-primary flex-1">
              Suivant →
            </button>
          ) : (
            <button onClick={onClose} className="btn btn-primary flex-1">
              C'est parti !
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
